import React, { useMemo, useState } from "react";
import { formatMetric, formatNumber } from "./clusterFormat";
import { compare, trendStyle } from "./seoTrends";
import { useKeywordComparison } from "./useSeoSnapshots";

// The expanded body of a cluster row: the individual keywords behind the
// cluster's aggregate figures.
//
// Why this matters: a cluster average can hide its best keyword entirely. In
// the live data `personalized-video` averages position ~4 while the keyword
// "personalized birthday video for kids" sits at 1.5 — invisible until you
// open the row.
//
// Compares the SAME two dates as the parent row, so a child can never
// contradict the aggregate above it.

/** Rank keywords by current impressions, then by position for the unranked. */
function sortKeywords(rows) {
  return [...rows].sort((a, b) => {
    if (b.impressionsAfter !== a.impressionsAfter) {
      return b.impressionsAfter - a.impressionsAfter;
    }
    // Nulls last — "never ranked" is not a good position.
    if (a.positionAfter === null) return 1;
    if (b.positionAfter === null) return -1;
    return a.positionAfter - b.positionAfter;
  });
}

/**
 * Build per-keyword before/after rows for one cluster.
 * Reuses `compare` so keyword trends use identical thresholds to cluster
 * trends — a keyword must not read "improving" while its cluster reads "flat".
 */
function buildRows(rowsByDate, fromDate, toDate, clusterId) {
  const index = (date) =>
    new Map(
      (rowsByDate[date] || [])
        .filter((k) => k.clusterId === clusterId)
        .map((k) => [k.term, k])
    );

  const first = index(fromDate);
  const last = index(toDate);
  const terms = new Set([...first.keys(), ...last.keys()]);

  return [...terms].map((term) => {
    const a = first.get(term);
    const b = last.get(term);
    return {
      term,
      volume: b?.volume ?? a?.volume ?? null,
      kd: b?.kd ?? a?.kd ?? null,
      matchedQueries: b?.matchedQueries ?? 0,
      ...compare(
        a ? { clicks: a.clicks, impressions: a.impressions, avgPosition: a.avgPosition } : null,
        b ? { clicks: b.clicks, impressions: b.impressions, avgPosition: b.avgPosition } : null
      ),
    };
  });
}

const HEADERS = ["Keyword", "Clicks", "Impressions", "Position", "Volume", "KD"];

const ClusterKeywordRows = ({ clusterId, fromDate, toDate, colSpan }) => {
  const { rows: rowsByDate, loading, error } = useKeywordComparison(fromDate, toDate);
  const [showUnranked, setShowUnranked] = useState(false);

  const all = useMemo(
    () => sortKeywords(buildRows(rowsByDate, fromDate, toDate, clusterId)),
    [rowsByDate, fromDate, toDate, clusterId]
  );

  // Most tracked keywords have no impressions yet — that is the honest day-one
  // baseline, but showing 70+ zero rows by default would bury the ones that
  // matter. They stay one click away rather than being hidden outright.
  const isLive = (k) => k.impressionsAfter > 0 || k.positionAfter !== null;
  const ranked = all.filter(isLive);
  const unranked = all.filter((k) => !isLive(k));
  const visible = showUnranked ? [...ranked, ...unranked] : ranked;

  return (
    <tr className="bg-gray-50">
      <td colSpan={colSpan} className="px-6 py-4">
        {loading && <p className="text-xs text-gray-500">Loading keywords…</p>}
        {error && <p className="text-xs text-red-600">{error}</p>}

        {!loading && !error && all.length === 0 && (
          <p className="text-xs text-gray-500 italic">
            No keyword rows recorded for this cluster in the selected range.
          </p>
        )}

        {!loading && !error && all.length > 0 && (
          <>
            {ranked.length === 0 && !showUnranked && (
              <p className="text-xs text-gray-500 italic mb-2">
                None of this cluster's {unranked.length} keywords are ranking yet.
              </p>
            )}

            {visible.length > 0 && (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    {HEADERS.map((h, i) => (
                      <th
                        key={h}
                        className={`py-1.5 pr-4 font-medium ${i === 0 ? "" : "text-right"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((kw) => {
                    const pos = trendStyle(kw.positionTrend);
                    return (
                      <tr key={kw.term} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-4 text-gray-800">{kw.term}</td>
                        <td className="py-1.5 pr-4 text-right text-gray-600">
                          {formatNumber(kw.clicksAfter)}
                        </td>
                        <td className="py-1.5 pr-4 text-right text-gray-600">
                          {formatNumber(kw.impressionsBefore)} → {formatNumber(kw.impressionsAfter)}
                        </td>
                        {/* null stays an em dash — never rendered as position 0. */}
                        <td className={`py-1.5 pr-4 text-right whitespace-nowrap ${pos.className}`}>
                          {formatMetric(kw.positionBefore)} → {formatMetric(kw.positionAfter)} {pos.arrow}
                        </td>
                        <td className="py-1.5 pr-4 text-right text-gray-600">
                          {formatNumber(kw.volume)}
                        </td>
                        <td className="py-1.5 pr-4 text-right text-gray-600">
                          {formatMetric(kw.kd)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {unranked.length > 0 && (
              <button
                onClick={() => setShowUnranked((v) => !v)}
                className="mt-2 text-[11px] font-medium text-gray-500 hover:text-gray-800 transition-colors"
              >
                {showUnranked
                  ? "Hide keywords that are not ranking"
                  : `+ ${unranked.length} keyword${unranked.length === 1 ? "" : "s"} not ranking yet`}
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

export default ClusterKeywordRows;
